/** Deal Inbox (v1 deal-inbox.js) — turn deal emails / texts / Facebook posts
 * into leads via the server AI parser. All work is server-side on the shared
 * Render backend (the SAME endpoints v1 hits); v2 just calls them with the
 * Supabase bearer + org_id, the way mail/comps/permits already do.
 *
 * OAuth: the Gmail scan needs a connected deal-email Gmail (Tom's OAuth step,
 * opened from the page). LIVE-FIRE: parse-text / parse-image / scan-gmail all
 * CREATE real leads in the shared properties pipeline, so the page gates them
 * behind liveFire `dealInbox` until the supervised enable. Reads (status) are
 * always safe. */
import { backendFetch, type BackendError } from "../../lib/backend";
import { API_BASE } from "../../config";

export interface InboxStatus {
  aiConfigured: boolean;
  gmailConnected: boolean;
  gmailScope: boolean;
  gmailOauthConfigured: boolean;
  senders: string[];
}

export function narrowStatus(raw: unknown): InboxStatus {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const sraw = o["senders"];
  return {
    aiConfigured: o["ai_configured"] === true,
    gmailConnected: o["gmail_connected"] === true,
    gmailScope: o["gmail_scope"] === true,
    gmailOauthConfigured: o["gmail_oauth_configured"] === true,
    senders: Array.isArray(sraw) ? sraw.filter((x) => x != null).map((x) => String(x)).filter(Boolean) : [],
  };
}

export async function fetchInboxStatus(token: string | null, signal?: AbortSignal): Promise<InboxStatus> {
  const j = await backendFetch(token, "/api/inbox/status", { timeoutMs: 30000, signal });
  return narrowStatus(j);
}

/** The result shape shared by parse-text / parse-image (v1's j). */
export interface ParseResult {
  created: boolean;
  address: string;
  reason: string; // "" | "duplicate" | "not_a_deal" | "no_text_in_image" | ...
  deal: Record<string, unknown>;
  transcript: string;
}

function narrowParse(raw: unknown): ParseResult {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const deal = o["deal"];
  return {
    created: o["created"] === true,
    address: typeof o["address"] === "string" ? o["address"] : "",
    reason: typeof o["reason"] === "string" ? o["reason"] : "",
    deal: deal && typeof deal === "object" && !Array.isArray(deal) ? (deal as Record<string, unknown>) : {},
    transcript: typeof o["transcript"] === "string" ? o["transcript"] : "",
  };
}

/** Parse pasted email/text/Facebook body → lead (v1 inboxParsePaste / fbDropParse). */
export async function parseText(
  token: string | null, orgId: string, body: string,
  source: "Manual" | "Facebook", sender?: string, signal?: AbortSignal,
): Promise<ParseResult> {
  const j = await backendFetch(token, "/api/inbox/parse-text", {
    method: "POST",
    body: { org_id: orgId, body, source, ...(sender ? { sender } : {}) },
    timeoutMs: 90000, signal,
  });
  return narrowParse(j);
}

/** Parse a Facebook screenshot (data URL) → lead (v1 fbDropImage). */
export async function parseImage(
  token: string | null, orgId: string, imageB64: string, mime: string, poster?: string, signal?: AbortSignal,
): Promise<ParseResult> {
  const j = await backendFetch(token, "/api/inbox/parse-image", {
    method: "POST",
    body: { org_id: orgId, image_b64: imageB64, mime: mime || "image/jpeg", source: "Facebook", ...(poster ? { poster } : {}) },
    timeoutMs: 90000, signal,
  });
  return narrowParse(j);
}

export interface ScanResult {
  createdCount: number;
  priceDropCount: number;
  scanned: number;
  errors: string[];
}

/** Scan the connected deal-email Gmail (v1 inboxScanNow). Needs Gmail OAuth. */
export async function scanGmail(token: string | null, orgId: string, maxResults = 40, signal?: AbortSignal): Promise<ScanResult> {
  const j = await backendFetch(token, "/api/inbox/scan-gmail", {
    method: "POST",
    body: { org_id: orgId, max_results: maxResults, force: true },
    timeoutMs: 120000, signal,
  });
  const o = j && typeof j === "object" ? (j as Record<string, unknown>) : {};
  const errs = o["errors"];
  return {
    createdCount: Number(o["created_count"]) || 0,
    priceDropCount: Number(o["price_drop_count"]) || 0,
    scanned: Number(o["scanned"]) || 0,
    errors: Array.isArray(errs) ? errs.map((x) => String(x)) : [],
  };
}

/** The OAuth connect URL (opened in a new tab — the server runs the flow). */
export const gmailConnectUrl = (): string => API_BASE + "/api/inbox/gmail/login";

export type { BackendError };
